import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import receiversPollersService from '../services/receiversPollersService';
import './AddCorporateProfilePage.css';

const EMPTY_FORM = { name: '', description: '', kind: 'poller', status: 'active' };
const RETURN_PATH = '/admin/receivers-pollers';

// Handles both add (/admin/receivers-pollers/new) and edit (/admin/receivers-pollers/:id/edit).
const AddReceiverPollerPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;
    const { selectedPartner, selectedAccount, selectedHotel } = useAccount();
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(isEdit);

    useEffect(() => {
        if (!isEdit) return;
        receiversPollersService.get(id)
            .then(res => {
                const r = res.receiverPoller || {};
                setForm({
                    name: r.name || '',
                    description: r.description || '',
                    kind: r.kind || 'poller',
                    status: r.status || 'active',
                });
            })
            .catch(err => setErrors({ submit: err.response?.data?.message || err.message || 'Failed to load receiver/poller' }))
            .finally(() => setLoading(false));
    }, [id, isEdit]);

    const set = (field, value) => {
        setForm(f => ({ ...f, [field]: value }));
        setErrors(e => ({ ...e, [field]: undefined }));
    };

    const handleSave = async () => {
        if (!form.name.trim()) { setErrors({ name: 'Name is required' }); return; }
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description.trim() || null,
                kind: form.kind,
                status: form.status,
            };
            if (isEdit) {
                // Scope is not editable here — leave the record's partner/account/hotel untouched.
                await receiversPollersService.update(id, payload);
            } else {
                // New records scope to the most specific navbar entity, or global if none selected.
                const scope = selectedHotel ? { hotel_id: selectedHotel }
                    : selectedAccount ? { account_id: selectedAccount }
                    : selectedPartner ? { partner_id: selectedPartner }
                    : {};
                await receiversPollersService.create({ ...payload, ...scope });
            }
            navigate(RETURN_PATH);
        } catch (err) {
            setErrors({ submit: err.response?.data?.message || err.response?.data?.error?.message || 'Failed to save receiver/poller' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="acp-page">
            <button className="pp-back-btn" onClick={() => navigate(RETURN_PATH)}>← Back</button>
            <div className="acp-card">
                <div className="acp-card-header">
                    <h1 className="acp-title">{isEdit ? 'Edit' : 'Add'} Receiver / Poller</h1>
                </div>

                <div className="acp-divider" />

                {loading ? (
                    <div className="acp-body"><p style={{ color: '#6b7280' }}>Loading…</p></div>
                ) : (
                    <div className="acp-body">
                        <div className="acp-field">
                            <label className="acp-label">Name <span className="acp-required">*</span></label>
                            <input
                                className={`acp-input${errors.name ? ' acp-input-error' : ''}`}
                                type="text"
                                placeholder="e.g., OHIP push receiver"
                                value={form.name}
                                onChange={e => set('name', e.target.value)}
                                autoFocus
                            />
                            {errors.name && <span className="acp-error-msg">{errors.name}</span>}
                        </div>

                        <div className="acp-field">
                            <label className="acp-label">Description</label>
                            <textarea
                                className="acp-input"
                                rows={3}
                                placeholder="Optional description"
                                value={form.description}
                                onChange={e => set('description', e.target.value)}
                            />
                        </div>

                        <div className="acp-field">
                            <label className="acp-label">Kind <span className="acp-required">*</span></label>
                            <select className="acp-input" value={form.kind} onChange={e => set('kind', e.target.value)}>
                                <option value="poller">Poller</option>
                                <option value="websocket">Websocket</option>
                            </select>
                        </div>

                        <div className="acp-field">
                            <label className="acp-label">Status</label>
                            <select className="acp-input" value={form.status} onChange={e => set('status', e.target.value)}>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>
                )}

                {errors.submit && <div className="acp-error-msg" style={{ margin: '0 0 1rem' }}>{errors.submit}</div>}
                <div className="acp-footer">
                    <button className="acp-btn-cancel" onClick={() => navigate(RETURN_PATH)} disabled={saving}>Cancel</button>
                    <button className="acp-btn-create" onClick={handleSave} disabled={saving || loading}>
                        {saving ? 'Saving...' : (isEdit ? 'Save' : 'Create')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddReceiverPollerPage;
